import type { EnvironmentId } from "@afar-research/contracts";
import { ChevronLeftIcon, FileIcon, FolderIcon, HomeIcon } from "lucide-react";
import { useMemo, useState } from "react";

import {
  hostNativeComposerAttachment,
  type ComposerPathAttachment,
} from "~/lib/composerAttachments";
import { ensureBrowseDirectoryPath, getBrowseParentPath } from "~/lib/projectPaths";
import { randomUUID } from "~/lib/utils";
import { filesystemEnvironment } from "~/state/filesystem";
import { useComposerPathSearch, useDebouncedValue } from "~/state/queries";
import { useEnvironmentQuery } from "~/state/query";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  ENVIRONMENT_FILES_MAX_ROWS,
  type EnvironmentFileRow,
  environmentBrowseRows,
  environmentLocationLabel,
  environmentSearchRows,
  environmentStartDirectory,
  isEnvironmentPathQuery,
} from "./composerEnvironmentFiles.logic";

const PATH_QUERY_DEBOUNCE_MS = 120;

/**
 * Picks files and folders that already live on the thread's Environment. Each
 * pick attaches as its path on the Environment host, so nothing is uploaded and
 * the agent works on the real file — on a remote or cloud Environment as much
 * as on this machine.
 *
 * It opens on the thread checkout and browses the whole filesystem from there:
 * up past the checkout, into any folder, or to a typed path. Search words look
 * through the checkout's index only.
 */
interface ComposerEnvironmentFilesDialogProps {
  readonly environmentId: EnvironmentId;
  /** The thread's checkout on the Environment: its Thread Worktree or Primary Checkout. */
  readonly workspaceRoot: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly onAttach: (attachments: ReadonlyArray<ComposerPathAttachment>) => void;
}

export function ComposerEnvironmentFilesDialog({
  open,
  ...props
}: ComposerEnvironmentFilesDialogProps & { readonly open: boolean }) {
  return open ? <ComposerEnvironmentFilesDialogSession {...props} /> : null;
}

function rowAttachment(row: EnvironmentFileRow): ComposerPathAttachment {
  return hostNativeComposerAttachment(randomUUID(), {
    type: row.kind === "directory" ? "folder" : "file",
    name: row.name,
    path: row.path,
  });
}

function ComposerEnvironmentFilesDialogSession(props: ComposerEnvironmentFilesDialogProps) {
  const startDirectory = environmentStartDirectory(props.workspaceRoot);
  const [directory, setDirectory] = useState(startDirectory);
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<ReadonlyMap<string, EnvironmentFileRow>>(new Map());

  const query = search.trim();
  const pathQuery = isEnvironmentPathQuery(query, props.workspaceRoot);
  const wordSearch = query.length > 0 && !pathQuery;
  const debouncedQuery = useDebouncedValue(pathQuery ? query : "", PATH_QUERY_DEBOUNCE_MS);
  // A typed path browses that path; otherwise the current directory is listed.
  const browsePath = pathQuery ? debouncedQuery : directory;
  const browseResult = useEnvironmentQuery(
    wordSearch || browsePath.length === 0
      ? null
      : filesystemEnvironment.browse({
          environmentId: props.environmentId,
          input: { partialPath: browsePath, cwd: props.workspaceRoot, includeFiles: true },
        }),
  );
  // Search asks the Environment, so it also finds files a truncated listing left out.
  const searchResult = useComposerPathSearch({
    environmentId: props.environmentId,
    cwd: wordSearch ? props.workspaceRoot : null,
    query: wordSearch ? query : "",
  });
  const rows = useMemo(
    () =>
      wordSearch
        ? environmentSearchRows(props.workspaceRoot, searchResult.entries)
        : environmentBrowseRows(browseResult.data?.entries ?? []),
    [browseResult.data?.entries, props.workspaceRoot, searchResult.entries, wordSearch],
  );
  const visibleRows = rows.slice(0, ENVIRONMENT_FILES_MAX_ROWS);
  const listing = wordSearch
    ? searchResult
    : {
        error: browseResult.error,
        isPending: browseResult.isPending || (pathQuery && debouncedQuery !== query),
      };
  const parentDirectory = getBrowseParentPath(directory);

  const openDirectory = (path: string) => {
    setDirectory(ensureBrowseDirectoryPath(path));
    setSearch("");
  };

  const toggle = (row: EnvironmentFileRow) => {
    setSelection((current) => {
      const next = new Map(current);
      if (!next.delete(row.path)) next.set(row.path, row);
      return next;
    });
  };

  const attach = () => {
    props.onAttach([...selection.values()].map(rowAttachment));
    props.onOpenChange(false);
  };

  return (
    <Dialog open onOpenChange={props.onOpenChange}>
      <DialogPopup data-composer-environment-files-dialog>
        <DialogHeader>
          <DialogTitle>Attach from the Environment</DialogTitle>
          <DialogDescription className="truncate">{props.workspaceRoot}</DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-3">
            <Input
              value={search}
              placeholder="Search the checkout, or type a path like ~/ or /"
              aria-label="Search the Environment's files"
              onChange={(event) => setSearch(event.target.value)}
            />
            {query.length === 0 ? (
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Up one folder"
                  disabled={parentDirectory === null}
                  onClick={() => {
                    if (parentDirectory !== null) openDirectory(parentDirectory);
                  }}
                >
                  <ChevronLeftIcon />
                </Button>
                <span className="min-w-0 flex-1 truncate text-muted-foreground" title={directory}>
                  {environmentLocationLabel(directory, props.workspaceRoot)}
                </span>
                {directory !== startDirectory ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDirectory(props.workspaceRoot)}
                  >
                    <HomeIcon />
                    Thread checkout
                  </Button>
                ) : null}
              </div>
            ) : null}
            {listing.error !== null ? (
              <div className="rounded-md border border-destructive/40 p-4 text-sm text-destructive">
                {listing.error}
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {listing.isPending
                  ? "Reading the Environment's files…"
                  : query.length > 0
                    ? "Nothing matches that search."
                    : "This folder is empty."}
              </div>
            ) : (
              <ul className="max-h-72 space-y-0.5 overflow-auto" aria-label="Environment files">
                {visibleRows.map((row) => (
                  <li
                    key={row.path}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
                  >
                    <Checkbox
                      aria-label={`Attach ${row.path}`}
                      checked={selection.has(row.path)}
                      onCheckedChange={() => toggle(row)}
                    />
                    {row.kind === "directory" ? (
                      <FolderIcon className="size-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <FileIcon className="size-4 shrink-0 text-muted-foreground" />
                    )}
                    {row.kind === "directory" ? (
                      <button
                        type="button"
                        className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
                        title={row.path}
                        onClick={() => openDirectory(row.path)}
                      >
                        {row.label}
                      </button>
                    ) : (
                      <span className="min-w-0 flex-1 truncate text-sm" title={row.path}>
                        {row.label}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {rows.length > visibleRows.length ? (
              <p className="text-xs text-muted-foreground">
                Showing {visibleRows.length} of {rows.length}. Type more of a path to narrow it
                down.
              </p>
            ) : null}
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={selection.size === 0} onClick={attach}>
            {selection.size > 0 ? `Attach ${selection.size}` : "Attach"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
